import { Link } from 'react-router-dom';
import type { CurrentSeasonView, TierName } from '../../api/seasons';

/** Shared with later season components (Profile, PastSeason) — Platinum is
 *  the permanent-record tier awarded at season close, not reachable via the
 *  live `thresholds` this strip renders, but the palette lives here so
 *  siblings under this directory draw from one source. */
export const TIER_COLORS: Record<TierName | 'Platinum', string> = {
  Bronze: '#a86b3a',
  Silver: '#8d8a83',
  Gold: '#c9962b',
  Platinum: '#2f3b4a',
};

/** No tier reached yet — points below Bronze, or no personal standing at all.
 *  Exported so siblings (TierLadder, StandingsList) share the same grey. */
export const NEUTRAL_TIER_COLOR = '#78716c';

/** 'YYYY-MM' season key → full month name, e.g. "August". Exported so the
 *  season page can derive its own wordmark/switcher labels from the same
 *  logic rather than re-deriving it. */
export function monthLabel(seasonKey: string): string {
  const [year, month] = seasonKey.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  });
}

function daysLabel(days: number): string {
  return `${days} day${days === 1 ? '' : 's'} left`;
}

/** Circle-plus-ribbon medal, matching the seasons mockup exactly.
 *  `size` is the outer diameter in px; the glyph scales with it so the same
 *  medal reads at 26px beside the tier name and at 18px inline in the
 *  next-tier target. */
export function TierMedal({ color, size = 26 }: { color: string; size?: number }) {
  const glyph = Math.round(size * 0.54);
  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center rounded-full text-[#fffdf6]"
      style={{ backgroundColor: color, height: size, width: size }}
    >
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="9" r="6" />
        <path d="M8.5 14.5 7 22l5-2.5L17 22l-1.5-7.5" />
      </svg>
    </span>
  );
}

/**
 * The home-page season strip — first player-visible piece of the seasons
 * feature. Answers "where am I, and what's next?" in one glance, above the
 * three difficulty cards.
 *
 * Renders nothing when `season` is null/undefined so a failed or still-in-
 * flight query never blocks the boards below it (see Landing.tsx, which
 * passes `null` on query error).
 */
export function SeasonStrip({
  season,
  signedIn = true,
}: {
  season: CurrentSeasonView | null | undefined;
  /** Guests and signed-in players BOTH arrive here with `me === null` — one
   *  has not played this month, the other cannot earn at all. They need
   *  different copy, and only the caller knows which is which. */
  signedIn?: boolean;
}) {
  if (!season) return null;

  const { me } = season;
  const tierColor = me?.tier ? TIER_COLORS[me.tier] : NEUTRAL_TIER_COLOR;
  // A signed-out visitor and a signed-in player with no standing yet (both
  // `me === null`) read as "Season · <month>" rather than a tier — neither
  // has a rank to show.
  const tierLabel = me ? (me.tier ?? 'Unranked') : 'Season';

  const className =
    'block overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]';

  // Signed in, the whole strip is one tap through to the season. Signed out
  // it cannot be, because the guest CTA below is itself a link and an anchor
  // inside an anchor is invalid — so the month title carries the season link
  // instead, leaving the register CTA as its sibling.
  const body = (
    <>
      <div className="h-1.5" style={{ backgroundColor: tierColor }} aria-hidden="true" />
      <div className="px-4 pb-3.5 pt-3">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-[9px]">
            <TierMedal color={tierColor} />
            {signedIn ? (
              <span className="text-sm font-bold text-stone-900">
                {tierLabel} · {monthLabel(season.key)}
              </span>
            ) : (
              <Link
                to="/season"
                data-testid="season-strip-title-link"
                className="text-sm font-bold text-stone-900 underline-offset-2 hover:underline"
              >
                {tierLabel} · {monthLabel(season.key)}
              </Link>
            )}
          </div>
          <span className="text-xs text-stone-600">{daysLabel(season.days_remaining)}</span>
        </div>

        {me ? (
          <>
            <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-board-bg">
              <div
                data-testid="season-progress-fill"
                className="h-full rounded-full"
                style={{ width: `${me.progressPct}%`, backgroundColor: tierColor }}
              />
            </div>
            <div className="mt-[7px] flex items-center justify-between text-xs text-stone-600">
              <span className="tabular-nums">{me.points.toLocaleString('en-GB')} pts</span>
              {me.next ? (
                // The target tier's own medal, so the goal is identifiable at
                // a glance rather than only by reading its name. Kept inside
                // this span so the line's text content stays exactly
                // "<N> pts to <Tier>" — the medal carries no text.
                <span className="inline-flex items-center gap-1.5">
                  <TierMedal color={TIER_COLORS[me.next.name]} size={18} />
                  {`${(me.next.min - me.points).toLocaleString('en-GB')} pts to ${me.next.name}`}
                </span>
              ) : (
                <span>
                  Rank #{me.rank ?? '—'} of {season.total_players.toLocaleString('en-GB')}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="mt-2.5 flex items-center justify-between gap-3 text-xs text-stone-600">
            {/* Each tier carries its own colour here too, so a visitor who has
                never seen the ladder can tell bronze from gold before they
                have any standing of their own. */}
            <span data-testid="season-thresholds" className="flex flex-wrap items-center gap-x-2.5">
              {season.thresholds.map((t) => (
                <span key={t.name} className="inline-flex items-center gap-1">
                  <TierMedal color={TIER_COLORS[t.name]} size={14} />
                  {t.name} {t.min}
                </span>
              ))}
            </span>
            {signedIn ? (
              <span>Finish a board to start your season</span>
            ) : (
              // The honest version. Guest play writes AnonymousPlay rows, not
              // Life rows, and season points derive from Life — so finishing a
              // board signed out earns exactly nothing. Telling a guest it
              // "starts your season" invites them to do something that does
              // nothing.
              //
              // The offer is real and retroactive: signing up replays the
              // guest's day into actual lives (see guest-claim.service.ts), so
              // today's board genuinely comes with them.
              <Link
                to="/register"
                data-testid="season-guest-cta"
                className="font-semibold text-emerald-800 underline underline-offset-2"
              >
                Signed-out play doesn't count — create an account
              </Link>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (!signedIn) {
    return (
      <div data-testid="season-strip" className={className}>
        {body}
      </div>
    );
  }

  return (
    <Link to="/season" data-testid="season-strip" aria-label="View season" className={className}>
      {body}
    </Link>
  );
}
